import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/auth/AuthProvider';
import { getKitStatus, putKit, type KitStatus } from '../lib/recovery/recoveryApi';
import {
  formatKit, generateKitCode, readOwnerPrivateKeyBytes, wrapKeyForKit,
} from '../lib/recovery/recoveryKit';
import './recovery.css';

/**
 * "Your Recovery Kit" — twenty-four characters that get you back in.
 *
 * The screen is built around one hard fact: we show the code exactly once and
 * can never show it again, because we never learn it. Everything here follows
 * from that — the code is large enough to copy by hand, printing is one
 * button, and the member has to confirm they have kept it before the screen
 * will let go of it.
 *
 * The alternative — a kit we could re-show — would mean a kit we could open,
 * which would mean the vault is only as private as our own database. That is
 * the trade being made, and the copy says so rather than hiding it.
 */
export default function RecoveryKitScreen() {
  const { api, user } = useAuth();
  const [status, setStatus] = useState<KitStatus | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [kept, setKept] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    getKitStatus(api).then(setStatus).catch(() => setStatus({ hasKit: false, hint: null, createdAt: null }));
  }, [api]);

  const make = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const email = user?.email;
      if (email === undefined) throw new Error('Sign in again before making a kit.');

      // The kit can only be made on a device that HAS the key. On any other
      // device there is nothing to wrap, and a kit that wrapped nothing would
      // be worse than no kit — it would look like protection.
      const priv = await readOwnerPrivateKeyBytes(email);
      if (priv === null) {
        throw new Error(
          'This device does not hold your key, so it cannot make a kit. Use the device you first set up with.',
        );
      }

      const fresh = generateKitCode();
      const wrapped = await wrapKeyForKit(priv, fresh);
      await putKit(api, wrapped);
      setCode(fresh);
      setKept(false);
      setDone(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not make your kit.');
    } finally {
      setBusy(false);
    }
  }, [api, user]);

  // Shown once. Once the member confirms they have it, it leaves the page —
  // and there is nowhere it could be recovered from, which is the point.
  if (code !== null && !done) {
    return (
      <section className="vault recovery">
        <p className="vault__kicker">Keep this somewhere safe</p>
        <h1 className="vault__title">Your Recovery Kit</h1>
        <p className="vault__sub">
          These twenty-four characters can get you back into your vault if you lose your phone.
          This is the only time they will ever be shown. We do not keep a copy — not because of a
          policy, but because we genuinely never see it.
        </p>

        <div className="recovery__code" aria-label="Your recovery kit code">
          {formatKit(code).split('-').map((group) => (
            <span key={group} className="recovery__group">{group}</span>
          ))}
        </div>

        <div className="recovery__actions">
          <button type="button" className="la-btn-secondary" onClick={() => window.print()}>
            Print it
          </button>
          <button
            type="button"
            className="la-btn-secondary"
            onClick={() => void navigator.clipboard?.writeText(formatKit(code))}
          >
            Copy it
          </button>
        </div>

        <p className="recovery__where">
          Somewhere safe means with your will, in a home safe, or with the person who would handle
          your affairs. Not a photo on the phone this is protecting.
        </p>

        <label className="recovery__confirm">
          <input type="checkbox" checked={kept} onChange={(e) => setKept(e.target.checked)} />
          <span>I have written this down or printed it.</span>
        </label>

        <button
          type="button"
          className="la-btn-primary"
          disabled={!kept}
          onClick={() => { setDone(true); setCode(null); setStatus(null); getKitStatus(api).then(setStatus).catch(() => {}); }}
        >
          Done
        </button>
      </section>
    );
  }

  return (
    <section className="vault recovery">
      <p className="vault__kicker">If you lose your phone</p>
      <h1 className="vault__title">Your Recovery Kit</h1>

      {status?.hasKit === true && status.stale === true && (
        <div className="la-card recovery__card recovery__stale">
          <p className="vault__section-label">This kit is out of date</p>
          <p className="vault__sub">
            You have added a device since you made it. The old kit would still open, but it would
            give this device the wrong key — you would be signed in and still unable to read your
            replies. Make a new one and replace the copy you kept.
          </p>
        </div>
      )}

      {status?.hasKit === true ? (
        <>
          <p className="vault__sub">
            You made a kit{status.createdAt !== null ? ` on ${new Date(status.createdAt).toLocaleDateString()}` : ''}.
            It ends in <strong>{status.hint}</strong> — check that against the copy you kept, so you know
            which piece of paper is the current one.
          </p>
          <div className="la-card recovery__card">
            <p className="vault__section-label">Make a new one</p>
            <p className="vault__sub">
              A new kit replaces the old one immediately. Do this if you think anyone else has seen
              it — the old characters stop working the moment the new ones appear.
            </p>
            <button type="button" className="la-btn-secondary" onClick={() => void make()} disabled={busy}>
              {busy ? 'Making…' : 'Make a new kit'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="vault__sub">
            Your vault is meant to last decades. Phones do not. If you sign in on a new phone and it
            does not recognise you, this kit is what gets you back in — on your own, straight away.
          </p>
          <p className="vault__sub">
            Without one you can still recover, but it takes three days and your advisor and guardians
            are told. The kit is the quick way.
          </p>
          {error !== '' && <p className="auth__alert" role="alert">{error}</p>}
          <button type="button" className="la-btn-primary" onClick={() => void make()} disabled={busy}>
            {busy ? 'Making your kit…' : 'Make my Recovery Kit'}
          </button>
        </>
      )}

      {error !== '' && status?.hasKit === true && <p className="auth__alert" role="alert">{error}</p>}

      <p className="recovery__back">
        <Link to="/you">Back to your account</Link>
      </p>
    </section>
  );
}
